import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getMetaList } from "../../api/design/meta";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import PageHeader from "../../components/common/ui/PageHeader";
import { formatDate } from "../../utils/date";

const LABELS = {
  fcr: "설계변경 요청",
  fcn: "설계변경 현장 통보",
  dcn: "설계사 통보",
};

const DOC_TYPE_LABEL = {
  fcr: "요청",
  fcn: "통보",
  dcn: "설계통보",
};

const STATUS_LABEL = {
  approved : "승인",
  rejected : "반려",
  submit : "상신",
  is_progress : "진행중"
}


export default function FcrList() {
  const navigate = useNavigate();
  const { id: project, docType } = useParams(); // docType: 'all' | 'fcr' | 'fcn' | 'dcn' 예상

  // URL 파라미터 정규화
  const normalizedDocType = (docType || "all"); // ALL / FCR / FCN / DCN

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 검색/필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [loading, setLoading] = useState(false);

  // 서버 호출
  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        project,
        [searchField]: q,
        start_date: startDate,
        end_date: endDate,
        page,
        page_size: pageSize,
      };

      // ALL 이 아니면 doc_type 필터 추가
      if (normalizedDocType !== "ALL") {
        params.doc_type = normalizedDocType;
      }

      const data = await getMetaList(params);

      setRows(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("FCR 목록 로드 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  // 최초 로드 + 필터 변경 시
  useEffect(() => {
    if (project) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, normalizedDocType, page, pageSize, searchTrigger]);

  // 검색 실행
  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  // --------------------------
  // 계층형 트리 flatten 처리
  // --------------------------
  const flattenDocs = (row, level = 0) => { 
    const current = { ...row, level };
    const children = (row.related_docs || []).flatMap((child) =>
      flattenDocs(child, level + 1)
    );
    return [current, ...children];
  };

  const flattenedRows = rows.flatMap((r) => flattenDocs(r));

  // 제목 문구
  const currentTitle =
    normalizedDocType === "all"
      ? "설계변경 문서 목록"
      : `${LABELS[normalizedDocType] || "설계변경"} 문서 목록`;

  // 새 문서 등록 시 사용할 타입 (ALL이면 기본 FCR로)
  const createDocType =
    (normalizedDocType === "all" || normalizedDocType === "fcr" || normalizedDocType === "fcn")
      ? "fc"
      : normalizedDocType.toLowerCase(); // fcr / fcn / dcn

  // ---------------------------------------------------
  // 컬럼 정의
  // ---------------------------------------------------
  const columns = [
    {
      key: "title",
      header: "제목",
      width: "500px",
      align: "left",
      headerAlign: "center",
      render: (row) => (
        <div style={{ paddingLeft: `${row.level * 16}px` }}>
          {row.level > 0 && "↳ "}
          <span className="text-gray-500 mr-1">[{DOC_TYPE_LABEL[row.doc_type] || row.doc_type}]</span>
          {row.title}
        </div>
      ),
    },
    // {
    //   key: "requestDept",
    //   header: "부서",
    //   render: (row) => {
    //     const t = row.doc_type; // FCR / FCN / DCN

    //     if (t === "FCR") {
    //       return row.meta?.requestDept || "-";
    //     }

    //     if (t === "FCN") {
    //       const list = row.meta?.requestDeptList || [];
    //       return list.length
    //         ? list.map((d) => `${d.company}(${d.name})`).join(", ")
    //         : "-";
    //     }

    //     if (t === "DCN") {
    //       return row.meta?.issueDept || "-";
    //     }

    //     return "-";
    //   },
    // },
    {
      key: "state",
      header: "상태",
      render: (row) => STATUS_LABEL[row.status] || "-",
    },
    {
      key: "created_by",
      header: "작성자",
      render: (row) => row.created_by?.name || "-",
    },
    {
      key: "created_at",
      header: "작성일",
      render: (row) => (row.created_at ? formatDate(row.created_at) : "-"),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader>{currentTitle}</PageHeader>

      <BoardToolbar
        right={
          <>
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
            <button
              className="btn-create"
              onClick={() =>
                navigate(
                  `/project/${project}/design/${createDocType}/new`
                )
              }
            >
              새 문서 등록
            </button>
          </>
        }
      />

      {/* 테이블 */}
      <BoardTable
        columns={columns}
        rows={flattenedRows}
        loading={loading}
        keyField="doc_dns"
        onRowClick={(row) =>
          navigate(
            `/project/${project}/design/${row.doc_type?.toLowerCase()}/${row.doc_dns}/view`
          )
        }
      />

      {/* 페이지네이션 */}
      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
}
